import React from 'react';
import { 
  Clock, 
  Target,
  Calendar,
  Rocket,
  BarChart3,
  FileText,
  Settings,
  CheckCircle,
  ClipboardList
} from 'lucide-react';

const Feb9Report = () => {
  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <Calendar className="w-8 h-8 text-blue-600" />
          Status Update - February 9th, 2025
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            <Calendar className="w-4 h-4" />
            February 9, 2025
          </span>
          <span className="meta-item">
            <Target className="w-4 h-4" />
            Executive Summary
          </span>
          <span className="meta-item">
            <Clock className="w-4 h-4" />
            Last Two Weeks (Jan 27 - Feb 9)
          </span>
        </div>
      </div>

      <div className="report-grid">
        {/* Wrap */}
        <div className="report-section">
          <h2 className="section-title">
            <Rocket className="w-6 h-6 text-green-600" />
            Wrap
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Status & Progress</h3>
                <span className="status-badge completed">ON TRACK</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Dev API:</strong> ✅ Shared for integration & development</li>
                <li><strong>Rate page detection:</strong> ✅ Completed (BERT + regex)</li>
                <li><strong>WC extraction:</strong> 🔄 80% completed</li>
                <li><strong>Other 3 coverages:</strong> 🔄 Implementation started</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Dev API Details</h3>
                <span className="status-badge completed">AVAILABLE</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Server:</strong> Dev</li>
                <li><strong>URL:</strong> <code>http://14.194.133.90:5005/wrap_rates</code></li>
                <li><strong>Method:</strong> POST — Body: Form-data — File: Upload PDF</li>
                <li><strong>Open:</strong> Bert model may check with HuggingFace as API; daily highlights via chat/sheet offered for tracking</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Asuretify */}
        <div className="report-section">
          <h2 className="section-title">
            <FileText className="w-6 h-6 text-indigo-600" />
            Asuretify
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Additional Interest</h3>
                <span className="status-badge in-progress">IN PROGRESS</span>
              </div>
              <ul className="achievement-list">
                <li>Explored existing pipeline</li>
                <li>Able to get DOO & Certificate holder OCR data</li>
                <li>Started "additional remark" (COI 2nd page) detection using regex</li>
                <li><strong>Next:</strong> 4.0 mini prompts for extraction of additional insured details</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Manuscript Endorsement — Testing Phase</h3>
                <span className="status-badge in-progress">TESTING</span>
              </div>
              <ul className="achievement-list">
                <li>Stopped prompt updates; shifted to testing</li>
                <li>Target: 50–60 documents by EOD</li>
                <li>Sample: 11 files (94 endorsement pages) tested</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Manuscript Endorsement — Accuracy (Partial)</h3>
                <span className="status-badge scheduled">REVIEW</span>
              </div>
              <ul className="achievement-list">
                <li>AdditionalInsuredLocation ~71%, AdditionalInsuredName ~78%</li>
                <li>FormNumber 84.6%, FormTitle 74.5%, FormTitleCategory 89.3%</li>
                <li>NoticeDays 88.4%, PageNo 86.2%, PolicyNumber 86.5%, PolicyType 86.5%</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Manuscript Endorsement — Issue & Solution</h3>
                <span className="status-badge urgent">ACTION</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Issue:</strong> AdditionalInsuredName & AdditionalInsuredLocation at ~70–75% accuracy</li>
                <li><strong>Proposed:</strong> Separate LLM call for these 2 fields (successful in Kinetic)</li>
                <li><strong>Team suggestion:</strong> CAG (Context Aggregation Group) — reduces LLM load, feeds only relevant context</li>
                <li><strong>Plan:</strong> Host to start CAG; others continue testing to capture scenarios and failures</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Other Items */}
        <div className="report-section">
          <h2 className="section-title">
            <Settings className="w-6 h-6 text-teal-600" />
            Other Items
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Ongoing Items</h3>
                <span className="status-badge scheduled">TBD</span>
              </div>
              <ul className="achievement-list">
                <li><strong>low_cost:</strong> Not heavily used; reported infra/scalability issues — TBD: verify and update</li>
                <li><strong>Batching:</strong> Question raised: should pages be batched? — TBD: check with relevant person</li>
                <li><strong>Azure Repo:</strong> Disabled; confirmation asked: has everyone moved over?</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Items */}
        <div className="report-section">
          <h2 className="section-title">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Action Items
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Summary</h3>
                <span className="status-badge in-progress">OPEN</span>
              </div>
              <ul className="achievement-list">
                <li>TBD: Check low_cost usage and provide updates</li>
                <li>TBD: Check with team on batching pages</li>
                <li>TBD: Start CAG for Manuscript Endorsement (AdditionalInsuredName & AdditionalInsuredLocation)</li>
                <li>TBD: Optional — daily discussion highlights via chat/sheet</li>
                <li>Team: Continue Manuscript Endorsement testing to identify scenarios and failures</li>
                <li>Team: Start CoverageIntent prompts</li>
                <li>Wrap team: Integrate and test against dev API</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Presentations to Be Done */}
        <div className="report-section">
          <h2 className="section-title">
            <ClipboardList className="w-6 h-6 text-amber-600" />
            Presentations to Be Done (Carried from Last Week)
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Critical / Urgent</h3>
                <span className="status-badge urgent">PRIORITY</span>
              </div>
              <ul className="achievement-list">
                <li>Start CAG for Manuscript Endorsement (AdditionalInsuredName & AdditionalInsuredLocation)</li>
                <li>Complete 15-document model comparison; switch to fine-tuned model if superior</li>
                <li>Whisperer batching — batch size to 1; investigate missing pages</li>
                <li>Start CoverageIntent prompts</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>High Priority</h3>
                <span className="status-badge scheduled">UPCOMING</span>
              </div>
              <ul className="achievement-list">
                <li>Check low_cost usage and provide updates</li>
                <li>Check with team on batching pages</li>
                <li>AWS cost reconciliation — download and analyze usage reports</li>
                <li>Implement 4.1 Mini model and batch processing</li>
                <li>Fix critical UI bugs in Kinetic/Asuretify demos</li>
                <li>Establish weekly Friday reports (cost + accuracy)</li>
                <li>Set up Azure storage; begin log migration</li>
                <li>Wrap team: Integrate and test against dev API</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Presentation Deliverables</h3>
                <span className="status-badge in-progress">TRACKING</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Weekly leadership report</strong> — cost + accuracy (every Friday)</li>
                <li><strong>low_cost usage update</strong> — for stakeholders</li>
                <li><strong>Batching recommendation</strong> — for team</li>
                <li><strong>Model comparison results</strong> — after 15-doc test</li>
                <li><strong>Usage data report</strong> — multiple COIs for leadership</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Meeting Notes */}
        <div className="report-section">
          <h2 className="section-title">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Meeting Notes
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Recent Meetings</h3>
                <span className="status-badge completed">RECORDED</span>
              </div>
              <ul className="achievement-list">
                <li><strong>Jan 30 (Thu):</strong> ~2m 40s — Host missed Teams notification; participant unavailable; async follow-up</li>
                <li><strong>Feb 7 (Fri):</strong> ~37m 49s — Main discussion on Wrap and Asuretify updates</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="report-footer">
        <p><strong>Report Date:</strong> February 9, 2025</p>
        <p><strong>Period:</strong> January 27 - February 9, 2025 (Last Two Weeks)</p>
        <p><strong>Source:</strong> Chat highlights extracted from meeting recordings</p>
        <p><strong>Presentations to do:</strong> See <code>ai-bimonthly/presentations-to-do.md</code> for full backlog</p>
      </div>
    </div>
  );
};

export default Feb9Report;
